import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { BroadcasterService } from 'ng-broadcaster';
import { from, of, pipe } from 'rxjs';
import { catchError, delay, filter, map, mergeMap, take, withLatestFrom } from 'rxjs/operators';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { GetAssociatedLocations, GetLocation } from '../actions/locations.actions';
import { GetPermissionsByTypes } from '../actions/permissions.actions';
import { GetSubscribedApps } from '../actions/apps.actions';
import { Organization } from '../../models/organization.class';
import { UtilsService } from '../../services/utils/utils.service';

import { ApiService } from '../../services/api/api.service';
import {
    CannotSwitchToOrganization,
    CanSwitchToOrganization,
    CheckOrganizationExists,
    CompletedSwitchToOrganization,
    GetApplicationMiscProperties,
    GetSessionDetails,
    SessionLogout,
    SessionLogoutSuccessfull,
    SetApplicationMiscProperties,
    SetCurrentOrganization,
    SetCustomerAdmin,
    SetProviderAdmin,
    SetSessionDetails,
    SwitchToOrganization,
} from '../actions/session.actions';
import { AlertError } from '../actions/shared.actions';
import { LoadUiPreferences } from '../actions/ui-preferences.actions';
import {
    getApplicationMiscPropertiesState,
    getAssociatedOrganizationsMap$,
    getCurrentOrganizationId$,
    getSessionDetails$,
    getSessionState$,
    isProviderAdmin$,
} from '../selectors';
import { SessionState } from '../states';

@Injectable()
export class SessionEffects {
    constructor(
        private store$: Store<SessionState>,
        private actions$: Actions,
        private _api: ApiService,
        private _store: Store,
        private _commonUtils: CommonUtilsService,
        private _broadcaster: BroadcasterService,
        private _router: Router,
        private _utils: UtilsService
    ) {}

    getSessionDetails$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetSessionDetails),
            withLatestFrom(this.store$.select(getSessionDetails$)),
            mergeMap(([action, session]) => {
                return this._api.session.getSessionDetails({ noAlerts: action.noAlerts }).pipe(
                    map(res => {
                        this.store$.dispatch(LoadUiPreferences());
                        if (res?.currentOrg.id !== session.currentOrganizationId && !action.noSwitch) {
                            this.store$.dispatch(
                                CompletedSwitchToOrganization({ organization: res?.currentOrg as Organization, switched: true })
                            );
                        }
                        return SetSessionDetails(res);
                    }),
                    catchError(errorResponse => {
                        this.store$.dispatch(
                            SetSessionDetails({
                                currentOrg: undefined,
                                isAdmin: undefined,
                                lastLogin: undefined,
                                loggedInOrg: undefined,
                                passwordExpiresInDays: undefined,
                                user: undefined,
                                timeZoneOffset: undefined,
                            })
                        );
                        return of(AlertError({ message: errorResponse?.msg }));
                    })
                );
            })
        );
    });

    getRolesByOrganizationId$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetSessionDetails),
            pipe(delay(100)),
            withLatestFrom(this.store$.select(isProviderAdmin$)),
            mergeMap(([action, providerAdmin]) => {
                if (providerAdmin !== undefined || !action.user?.id || !action.loggedInOrg?.id) {
                    return [];
                }
                return this._api.roles.getRolesByOrganizationId({ locationId: action.loggedInOrg?.id, userId: action.user?.id }).pipe(
                    map((res: any) => {
                        const providerRole = res?.response?.roles?.find(role => role.roleName === 'PROVIDER_ADMIN');
                        const customerRole = res?.response?.roles?.find(role => role.roleName === 'CUSTOMER_ADMIN');
                        this.store$.dispatch(SetCustomerAdmin({ customerAdmin: customerRole ? true : false }));
                        return SetProviderAdmin({ providerAdmin: providerRole ? true : false });
                        // return of(
                        //     SetProviderAdmin({ providerAdmin: providerRole ? true : false }),
                        //     SetCustomerAdmin({ customerAdmin: customerRole ? true : false })
                        // );
                    }),
                    catchError(errorResponse => of(AlertError({ message: errorResponse?.msg })))
                );
            })
        );
    });

    sessionLogout$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SessionLogout),
            mergeMap(() => {
                // Always preserve IdP domain and history across logout on the client
                const preservedDomain = this._commonUtils.getFromStorage('domain');
                const preservedIdpHistory = this._commonUtils.getFromStorage('idpHistory');
                this._commonUtils.clearStorage();
                preservedDomain && this._commonUtils.setInStorage('domain', preservedDomain);
                preservedIdpHistory && this._commonUtils.setInStorage('idpHistory', preservedIdpHistory);
                return this._api.session.logout().pipe(
                    map(() => SessionLogoutSuccessfull()),
                    catchError(errorResponse => {
                        errorResponse?.msg && this._store.dispatch(AlertError({ message: errorResponse?.msg }));
                        return of(SessionLogoutSuccessfull());
                    })
                );
            })
        );
    });

    sessionLogoutSuccessfull$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SessionLogoutSuccessfull),
            mergeMap(() => {
                this._broadcaster.broadcast('logout', { noRedirect: true, hidePopup: true });
                return [];
            })
        );
    });

    getApplicationMiscProperties = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetApplicationMiscProperties),
            mergeMap(() => {
                let appSettings: SessionState['applicationMiscProperties'];
                this.store$
                    .pipe(select(getApplicationMiscPropertiesState))
                    .pipe(take(1))
                    .subscribe(data => (appSettings = data));
                if (appSettings?.['x-auth-token']) {
                    return [];
                }
                return from(this._api.session.getApplicationMiscProps()).pipe(
                    map(response => SetApplicationMiscProperties(response)),
                    catchError(errorResponse => of(AlertError({ message: errorResponse })))
                );
            })
        );
    });

    checkOrganizationExists$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CheckOrganizationExists),
            withLatestFrom(
                this.store$.select(getAssociatedOrganizationsMap$),
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getSessionState$)
            ),
            mergeMap(([action, organizations, currentOrganizationId, session]) => {
                if (!action.organizationId) {
                    this._utils.navigateToOrganization(undefined, action.switchToUI);
                    return [];
                }
                if (session === false) {
                    return [];
                }
                const organization = organizations?.[action.organizationId];
                const onNewUI = this._commonUtils.getCookie('new-ui') === 'true';
                const retryCount = action.retryCount || 0;
                if (!organization && (Object.keys(organizations)?.length || 0) < 2 && retryCount < 3) {
                    setTimeout(() => {
                        this.store$.dispatch(CheckOrganizationExists({ ...action, retryCount: retryCount + 1 }));
                    }, 1000);
                    if (onNewUI) {
                        return of(GetLocation({ organizationId: action.organizationId, update: false }));
                    } else {
                        return of(GetAssociatedLocations());
                    }
                } else if (!organization && action.organizationId?.length > 0 && organizations?.[currentOrganizationId]) {
                    this._utils.navigateToOrganization(undefined, action.switchToUI);
                    return [];
                }
                if (organization && !organization.noAccess) {
                    return of(CanSwitchToOrganization({ organizationId: action.organizationId }));
                }
                return of(CannotSwitchToOrganization({ failed: true, skipAlert: true }));
            })
        );
    });

    canSwitchToOrganization$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CanSwitchToOrganization),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getAssociatedOrganizationsMap$)),
            mergeMap(([action, organizationId, orgMap]) => {
                const stored = this._commonUtils.getFromStorage('currentOrganizationId');
                if (action.organizationId === stored && organizationId && action.organizationId !== organizationId) {
                    return of(SwitchToOrganization({ organizationId: stored }));
                }
                if (!organizationId && action.organizationId === stored) {
                    return of(CompletedSwitchToOrganization({ switched: false, organization: orgMap?.[action.organizationId] }));
                }
                if (action.organizationId === organizationId) {
                    return of(CompletedSwitchToOrganization({ switched: false, organization: orgMap?.[action.organizationId] }));
                }
                return of(SwitchToOrganization({ organizationId: action.organizationId }));
            })
        );
    });

    switchToOrganization$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SwitchToOrganization),
            withLatestFrom(this.store$.select(getAssociatedOrganizationsMap$)),
            mergeMap(([action, orgsMap]) => {
                [
                    'userPermissions',
                    'allusers',
                    'userpagesize',
                    'roles',
                    'selectedUserId',
                    'selectedUserRoles',
                    'rolePermissions',
                    'clickedUser',
                    'clickedrole',
                    'locations',
                    'selectedroleid',
                    'selectedrolepermissions',
                ].forEach(storedValue => {
                    this._commonUtils.removeFromStorage(storedValue);
                });
                return this._api.locations.switchToOrganization({ firstEvent: false, orgId: action.organizationId }).pipe(
                    map(() => {
                        const organization = orgsMap?.[action.organizationId] || new Organization({ id: action.organizationId });
                        return CompletedSwitchToOrganization({
                            switched: true,
                            organization,
                        });
                    }),
                    catchError(e => of(AlertError({ message: e })))
                );
            })
        );
    });

    switchedToOrganization$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CompletedSwitchToOrganization),
            mergeMap(action => {
                this.store$.dispatch(SetCurrentOrganization({ organization: action.organization }));
                this.store$.dispatch(
                    GetLocation({
                        organizationId: action.organization?.id,
                        update: false,
                    })
                );
                this.store$.dispatch(GetSubscribedApps());
                this.store$.dispatch(GetPermissionsByTypes({ permissionTypes: ['PROVIDER', 'CUSTOMER'] }));
                return [];
            })
        );
    });

    setCurrentOrganization$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetCurrentOrganization),
            withLatestFrom(this.store$.select(getAssociatedOrganizationsMap$)),
            mergeMap(([action, orgMap]) => {
                const organization = CommonUtilsService.cloneObject(orgMap[action.organization.id]);
                if (!organization) {
                    this._commonUtils.setInStorage('currentOrganizationId', action.organization.id);
                    this._broadcaster.broadcast('setStoreContext');
                    return [];
                }
                this.prepareParentMap(orgMap, organization, organization.parentId);
                this._commonUtils.setInStorage('currentOrganization', organization);
                this._commonUtils.setInStorage('currentOrganizationId', organization?.id);
                this._commonUtils.setInStorage('orgTimeZone', organization?.timeZone);
                this._broadcaster.broadcast('setStoreContext');
                return [];
            })
        );
    });

    prepareParentMap = (orgMap: { [property: string]: Organization }, org: Organization, parentId: string) => {
        if (parentId) {
            org.parent = org.parent || CommonUtilsService.cloneObject(orgMap[parentId]);
        }
        if (org.parent?.parentId) {
            this.prepareParentMap(orgMap, org.parent, org.parent.parentId);
        }
    };

    cannotSwitchToOrganization$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CannotSwitchToOrganization),
            filter(data => data.failed),
            mergeMap(data => {
                if (!data.skipAlert) {
                    this.store$.dispatch(AlertError({ message: data.message || 'User does not have access' }));
                }
                const orgId = this._commonUtils.getFromStorage('currentOrganizationId');
                this._router.navigate(['organizations', orgId]);
                return [];
            })
        );
    });
}
